import { useImportModal } from 'hooks/useImportModal'
import { useFolder } from 'hooks/queries/folders/useFolder'
import { handleLinkPaste } from 'components/CreateInlineMenu/utils'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CreateInlineMenu } from 'components/CreateInlineMenu/CreateInlineMenu'
import { NewInlineOption } from 'components/CreateInlineMenu/types'
import { handleKeydown } from 'components/CreateInlineMenu/utils'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useMemberProfile } from 'hooks/useMemberProfile'
import useNewEntrypointModal from 'hooks/useNewEntrypointModal'
import * as React from 'react'
import { ClipboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { HandbookItem, HandbookItemType, MentionableArticle, ViewType } from 'types'
import { reportError } from 'utils/error'
import { ADD_HANDBOOK_ITEM_EVENT, trackEvent } from 'utils/trackingUtils'
import { useHandbookItems } from '../HandbookItemProvider'
import Styles from './NewItem.module.scss'
import { useOnSelectAlgoliaItem } from './hooks'
import { autoUpdate, flip, offset, shift } from '@floating-ui/react-dom'
import { FloatingPortal, useDismiss, useFloating, useInteractions } from '@floating-ui/react-dom-interactions'
import { observer } from 'mobx-react-lite'
import { validUrlRegexp } from 'regexps'
import { LinkForm } from './Link'
import { useRootStore } from 'stores/RootStore'
import { BoardView, Document, FolderClosed, HandbookSection, Link, PlusCircled, TableView } from 'almanac-ui/icons'
interface Props {
  depth?: number
  /**
   * The position of the new item, already  1-based.
   */
  position: number
  folderKey?: string
  onClose: () => void
  onCreate: (newItem: HandbookItem, createAnother?: boolean) => void
  handbookId: number
  parentId?: number | null
}

export const NewItem = observer(
  ({ onClose, position, folderKey, handbookId, onCreate, parentId = null, depth = 0 }: Props) => {
    const auth = useAuth()
    const [query, setQuery] = useState('')
    const onSelectAlgoliaItem = useOnSelectAlgoliaItem(position)
    const { createItem, treeStore } = useHandbookItems()
    const { workspaceTemplatesStore: templates } = useRootStore()
    const { openImportModal } = useImportModal()
    const [chosenOption, setChosenOption] = useState<NewInlineOption | string>(NewInlineOption.Doc)
    const { openNewEntrypointModal } = useNewEntrypointModal()
    const originalDefaults = React.useMemo(() => {
      return treeStore?.isLegacy
        ? [NewInlineOption.Doc, NewInlineOption.UrlLink, NewInlineOption.Section]
        : [
            NewInlineOption.Doc,
            NewInlineOption.Handbook,
            NewInlineOption.Project,
            NewInlineOption.More,
            NewInlineOption.UrlLink,
            NewInlineOption.Section,
          ]
    }, [treeStore?.isLegacy])
    const [defaultListItems, setDefaultListItems] = useState<NewInlineOption[]>(originalDefaults)
    const workspace = useCurrentWorkspace()
    const { profile } = useMemberProfile(workspace?.id, auth?.id)
    const [listItems, setListItems] = useState<(NewInlineOption | string)[]>(originalDefaults)
    const [suggestedDocs, setSuggestedDocs] = useState<MentionableArticle[]>([])
    const folderQuery = useFolder(folderKey || null)
    const handbookFolder = folderQuery.data?.folder
    const [showMatchedDoc, setShowMatchedDoc] = useState(false)
    const [showLinkForm, setShowLinkForm] = useState(false)

    useEffect(() => {
      setTimeout(() => inputRef.current?.focus(), 0)
    }, [])

    useEffect(() => {
      setListItems([...defaultListItems, ...suggestedDocs.map((doc) => doc.key)])
    }, [defaultListItems, suggestedDocs])

    const updateListItems = useCallback(() => {
      return (articleKeys: string[]) => {
        setListItems([...defaultListItems, ...articleKeys])
      }
    }, [defaultListItems])

    useEffect(() => {
      if ((query.startsWith('@') || showMatchedDoc) && !!suggestedDocs.length) {
        setDefaultListItems([])
        return
      }

      setDefaultListItems(originalDefaults)
    }, [query, showMatchedDoc, suggestedDocs.length]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (!query.length && showMatchedDoc) setShowMatchedDoc(false)
    }, [query, showMatchedDoc])

    const createDoc = async (createAnother: boolean = false) => {
      if (treeStore?.isCreating) return

      const title = query.trim() || 'Untitled'
      treeStore?.setIsCreating(true)
      trackEvent(ADD_HANDBOOK_ITEM_EVENT, profile)

      const newItem = await createItem({
        type: HandbookItemType.Document,
        handbook_id: handbookId,
        title,
        parent_id: parentId,
        position,
      })

      if (newItem) {
        onCreate(newItem, createAnother)
      } else {
        reportError(new Error('Error while creating a new handbook doc item'), { metaData: { title } })
        showErrorMessageToast('Something went wrong 😢')
      }

      treeStore?.setIsCreating(false)
    }

    const createLink = async ({ title, url, favicon }: Pick<HandbookItem, 'title' | 'url' | 'favicon'>) => {
      trackEvent(ADD_HANDBOOK_ITEM_EVENT, profile)
      createItem({
        type: HandbookItemType.Link,
        title,
        url,
        favicon,
        position,
        handbook_id: handbookId,
      })
      onClose()
    }

    const createSection = async () => {
      trackEvent(ADD_HANDBOOK_ITEM_EVENT, profile)
      const newItem = await createItem({
        type: HandbookItemType.Section,
        title: query.trim() || 'Untitled',
        handbook_id: handbookId,
        position,
      })
      if (newItem) {
        onCreate(newItem, true)
      } else {
        showErrorMessageToast('Something went wrong 😢')
      }
    }

    const createFolder = async (createAnother: boolean = false) => {
      if (!workspace || treeStore?.isCreating) return

      treeStore?.setIsCreating(true)
      trackEvent(ADD_HANDBOOK_ITEM_EVENT, profile)
      const newItem = await createItem({
        type: HandbookItemType.NestedHandbook,
        title: query.trim() || 'Untitled',
        handbook_mode: false,
        database_mode: false,
        default_view: chosenOption === NewInlineOption.BoardView ? ViewType.Board : ViewType.List,
        parent_id: null, // should any folder have parent id null?
        position,
        handbook_id: handbookId,
      })

      if (newItem) {
        showToastMessage(`Folder "${newItem.title}" created`, 'success')
        onCreate(newItem, createAnother)
      } else {
        reportError('Failed to create new folder', {
          severity: 'error',
          metaData: {
            query,
            workspace,
          },
        })
      }
      treeStore?.setIsCreating(false)
    }

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
      handleLinkPaste({
        text: event.clipboardData?.getData('text'),
        overrideFolder: handbookFolder || null,
        onOpenImportModal: openImportModal,
        onDocMatch: (modifiedUrl) => {
          setQuery(modifiedUrl)
          setShowMatchedDoc(true)
        },
        showSuggestions: true,
      })
    }

    const inputRef = useRef<HTMLInputElement | null>(null)

    const openCreateNewModal = () => {
      templates.setNewItemPosition(position)
      openNewEntrypointModal({
        createInFolder: handbookFolder,
      })
      onClose()
    }

    const createSuggestedDoc = (id: string, title: string) => {
      onClose()
      onSelectAlgoliaItem({ key: id, title })
    }

    const handleInputKeydown = (e: React.KeyboardEvent) => {
      const shiftKey = e.shiftKey

      handleKeydown({
        event: e,
        listItems,
        chosenOption,
        setChosenOption,
        handleDocClick: () => {
          createDoc(shiftKey)
        },
        handleFolderClick: () => {
          createFolder(shiftKey)
        },
        handleMoreClick: openCreateNewModal,
        handleSectionClick: createSection,
        handleLinkClick: () => setShowLinkForm(true),
        handleSuggestedDocClick: () =>
          createSuggestedDoc(chosenOption, suggestedDocs.find((doc) => doc.key === chosenOption)?.title || 'This doc'),
        reset: onClose,
      })
    }

    const { x, y, reference, floating, context, refs } = useFloating({
      open: true,
      placement: 'right-start',
      strategy: 'fixed',
      middleware: [offset(4), shift({ padding: 4 }), flip()],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
        })
      },
    })
    const dismiss = useDismiss(context, {
      bubbles: true,
      outsidePressEvent: 'click',
      outsidePress: (event) => {
        const prompt = refs.floating.current
        const input = inputRef.current
        const target = event.target as HTMLElement
        if (prompt !== target && !prompt?.contains(target) && !input?.contains(target)) {
          onClose()
        }
        return false
      },
    })
    const { getFloatingProps } = useInteractions([dismiss])

    const iconMap = React.useMemo(
      () =>
        ({
          [NewInlineOption.Doc]: Document,
          [NewInlineOption.BoardView]: BoardView,
          [NewInlineOption.Database]: TableView,
          [NewInlineOption.Handbook]: FolderClosed,
          [NewInlineOption.Section]: HandbookSection,
          [NewInlineOption.UrlLink]: Link,
          [NewInlineOption.More]: PlusCircled,
        } as { [key: string]: typeof Document }),
      [],
    )

    if (showLinkForm || validUrlRegexp.test(query)) {
      return (
        <div
          className={Styles.linkFormWrapper}
          ref={reference}
          style={
            {
              '--indent-level': depth,
            } as React.CSSProperties
          }
        >
          <LinkForm url={query} onSubmit={createLink} onCancel={onClose} />
        </div>
      )
    }

    const Icon = iconMap[chosenOption] || Document

    return (
      <div
        className={Styles.inputWrapper}
        ref={reference}
        style={
          {
            '--indent-level': depth,
          } as React.CSSProperties
        }
      >
        <div className={Styles.newItemMenu}>
          <div className={Styles.icon}>
            <Icon />
          </div>
          <input
            ref={inputRef}
            placeholder={'Create, search, link...'}
            onKeyDown={handleInputKeydown}
            onChange={(e) => {
              if (!showMatchedDoc || !e.target.value.length) setQuery(e.target.value)
            }}
            onPaste={handlePaste}
            onBlur={(e) => {
              const prompt = refs.floating.current
              const target = e.relatedTarget as Node
              if (prompt !== target && !prompt?.contains(target)) {
                onClose()
              }
            }}
          />
        </div>
        <FloatingPortal id="floating-ui-root">
          <div ref={floating} style={{ position: 'fixed', top: y || 0, left: x || 0 }} {...getFloatingProps()}>
            <CreateInlineMenu
              close={onClose}
              onCreateDoc={() => {
                createDoc(false)
              }}
              onCreateFolder={() => {
                createFolder(false)
              }}
              listItems={listItems}
              isProcessing={!!treeStore?.isCreating}
              onCreateUrlLink={() => setShowLinkForm(true)}
              onCreateSection={createSection}
              onCreateSuggestedDoc={createSuggestedDoc}
              onOpenMore={openCreateNewModal}
              dropdownStyle={Styles.paddingTop}
              inputValue={query}
              chosenOption={chosenOption}
              setChosenOption={setChosenOption}
              updateListItems={updateListItems}
              suggestedDocs={suggestedDocs}
              setSuggestedDocs={setSuggestedDocs}
              showPasteText={!showMatchedDoc}
              showSuggestions
            />
          </div>
        </FloatingPortal>
      </div>
    )
  },
)
